/**
 * Lean & Clean onboarding tour — React provider + restart hook (Phase 5B).
 *
 * Wraps the app so any component can trigger or abort the guided
 * tour via `useLeanCleanTour()`. Internally uses `driver.js` for
 * the spotlight + popover; the step list and gating rules come
 * from the pure `src/features/onboarding/tour.ts` module so the
 * non-UI behaviour is unit-testable.
 *
 * The tour auto-starts once per user, after their first visit to
 * the dashboard (we wait for `useLocation().pathname === '/dashboard'`
 * so the sidebar + widgets the tour points at actually exist).
 * After completion or dismissal the version stamp lands in
 * `localStorage` so the tour does not re-open every refresh.
 *
 * Call `useLeanCleanTour().restart()` from anywhere (e.g. a
 * "Tour erneut starten"-button in Settings) to replay the tour
 * for the current user without clearing localStorage globally.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';

import { useFeatures } from '@/hooks/useFeatures';
import {
  LEAN_CLEAN_TOUR_STEPS,
  LEAN_CLEAN_TOUR_VERSION,
  readCompletedTourVersion,
  selectTourSteps,
  shouldAutoStartTour,
  writeCompletedTourVersion,
  type TourStep,
} from '@/features/onboarding/tour';
import { TourContext, type TourContextValue } from './useLeanCleanTour';

/**
 * Convert our pure-data step shape into the structure driver.js
 * expects. driver.js is happy to take a `null` element for a
 * centred popover, so the mapping is nearly one-to-one.
 */
function toDriverStep(step: TourStep) {
  return {
    element: step.element ?? undefined,
    popover: {
      title: step.title,
      description: step.description,
      side: 'bottom' as const,
      align: 'start' as const,
    },
  };
}

export function LeanCleanTourProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { hasFeature } = useFeatures();
  const driverRef = useRef<Driver | null>(null);

  // One driver instance per provider lifetime; the steps are
  // re-computed on every start so a navigation between pages picks
  // up the right subset (e.g. the patient-tab step appears only on
  // `/patients/:id`).
  const startTour = useCallback(() => {
    const steps = selectTourSteps(LEAN_CLEAN_TOUR_STEPS, {
      pathname: location.pathname,
      hasFeature,
    });
    if (steps.length === 0) return;

    // Destroy any prior run before re-creating — driver.js can only
    // drive one tour at a time.
    driverRef.current?.destroy();
    driverRef.current = driver({
      showProgress: true,
      allowClose: true,
      overlayOpacity: 0.5,
      nextBtnText: 'Weiter',
      prevBtnText: 'Zurück',
      doneBtnText: 'Fertig',
      progressText: '{{current}} / {{total}}',
      steps: steps.map(toDriverStep),
      onDestroyed: () => {
        // `onDestroyed` fires on both completion and close — either
        // way we record the current version so the auto-start flag
        // goes away.
        writeCompletedTourVersion(LEAN_CLEAN_TOUR_VERSION);
      },
    });
    driverRef.current.drive();
  }, [hasFeature, location.pathname]);

  const stopTour = useCallback(() => {
    driverRef.current?.destroy();
  }, []);

  const restartTour = useCallback(() => {
    // Reset the persisted marker then start; without the reset the
    // button would effectively be a second "start" (which still
    // works, but the Settings UI implies a clean slate).
    writeCompletedTourVersion(-1);
    startTour();
  }, [startTour]);

  // Auto-start: fire once, when we land on the dashboard, the user has
  // not completed this tour version yet, AND the onboarding wizard has
  // been finished (so the tour does not race the wizard).
  const WIZARD_STORAGE_KEY = 'nutriflow_onboarding_complete';
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStartedRef.current) return;
    if (location.pathname !== '/dashboard') return;
    // Do not start the tour before the onboarding wizard is complete.
    if (localStorage.getItem(WIZARD_STORAGE_KEY) !== 'true') return;
    const completed = readCompletedTourVersion();
    if (!shouldAutoStartTour(completed)) return;
    autoStartedRef.current = true;
    // Small delay — the dashboard widgets lazy-mount; racing the
    // mount would spotlight an empty hero bar.
    const id = window.setTimeout(() => startTour(), 600);
    return () => window.clearTimeout(id);
  }, [location.pathname, startTour]);

  // Destroy the driver on unmount so navigating away mid-tour does
  // not leak the overlay.
  useEffect(() => () => driverRef.current?.destroy(), []);

  const value = useMemo<TourContextValue>(
    () => ({ start: startTour, stop: stopTour, restart: restartTour }),
    [startTour, stopTour, restartTour],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}
